
import React, {useEffect, useReducer} from 'react';
import {createGlobalStyle} from 'styled-components';

import {useSelector, useDispatch} from 'react-redux';
import {hotelName as hotelNameSelectorFn} from '../../selectors';
import {defaultTheme} from 'build-theme';

// UTILS
import {getIntersectionObserverInstance} from '@ibibo/intersection-observer';

// CONFIG
import {googleMapsApiKey} from '../../../../../shared/config/app-config';

// TYPES
import {SelectedPlace, GoogleMapLocationType} from '../../../HotelsMap/redux/types';
import {updateSelectedPlaces} from '../../../HotelsMap/redux/actions';

const MapGlobalStyles = createGlobalStyle`
  /* GOOGLE MAPS STYLES FOR DETAILS */
  .pac-container {
    transform: translate(-2.2rem, 1.4rem);
    width: inherit !important;
    margin: 0 0 0 1.5rem;
  }
  /* don't show 'close' button since info window opens on mouseover */
  .gm-style-iw button {
    display: none!important;
  }
`;

const intersectionObserverOptions = {
  root: null,
  threshHold: 0.01,
};

const getInfoWindowContent = ({name}) => `
  <div id="content">
    <div id="siteNotice">
    </div>
    <h4 id="firstHeading" class="firstHeading">${name}</h4>
  <div>
`;

const cyanBlueAttractionsIcon = '//maps.google.com/mapfiles/kml/paddle/ltblu-circle.png';
const darkBlueTransitIcon = '//maps.google.com/mapfiles/kml/paddle/blu-circle.png';
const yellowUserSearchedIcon = '//maps.google.com/mapfiles/kml/paddle/ylw-circle.png';

const setInfoWindowOnMarker = ({
  googleMapNamespace,
  content,
  marker,
  hotelLocationMap,
  // showNearbyPlaces,
}) => {
  const infowindow = new googleMapNamespace.InfoWindow({
    content,
  });

  // add click listener in case when all the places marker are there, else open the name window anyway.
  let listeners = [];

  // if (!showNearbyPlaces) {
  //   infowindow.open(hotelLocationMap, marker);
  // }

  const mouseOverListener = marker.addListener('mouseover', () => {
    infowindow.open(hotelLocationMap, marker);
  });

  const mouseoutListener = marker.addListener('mouseout', function() {
    infowindow.close();
  });

  listeners = [mouseOverListener, mouseoutListener];

  return listeners;
};

// STYLES
import * as S from './GoogleMap.styles';

enum LocationMapActionTypes {
  updateGoogleMapRelatedInstancesAndNamespace = 'updateGoogleMapRelatedInstancesAndNamespace',
}

const locationMapInitState = {
  googleMapNamespace: null,
  autocompleteInstance: null,
};

// UTILS
// poi = point of interest === attractions and transit points
const setMarkersForAllPlaces = ({
  list = [],
  googleMapNamespace, // windows.google.maps
  hotelLocationMap,
}) => {
  let listOfMarkerReferences = [];

  let markersAndPlacesDict = {};

  list.forEach((place, index) => {
    if (!place?.crd) {
      return;
    }

    let conditionalOptions = {};

    let label: {text: string; color: string; fontWeight?: string} = {
      text: '',
      color: defaultTheme.colors.white.base,
      fontWeight: 'bold',
      // labelAnchor: new google.maps.Point(18, 12),
    };

    let image: {url: string; zIndex?: number} = {
      url: '//maps.google.com/mapfiles/kml/paddle/ylw-circle.png',
    };

    if (place.type === GoogleMapLocationType.attraction) {
      image = {
        url: cyanBlueAttractionsIcon,
      };

      label.text = 'A';
      label.color = defaultTheme.colors.black.base;
    }

    if (place.type === GoogleMapLocationType.transit) {
      image = {
        url: darkBlueTransitIcon,
      };

      label.text = 'T';
    }

    if (place.type === GoogleMapLocationType.userSelected) {
      image = {
        url: yellowUserSearchedIcon,
      };

      label.text = 'U';
    }

    conditionalOptions = {
      ...(image?.url ? {icon: image} : {}),
      ...(label ? {label} : {}),
    };

    const [lat, lng] = place.crd;

    const marker = new googleMapNamespace.Marker({
      position: {lat, lng},
      map: hotelLocationMap,
      ...conditionalOptions,
      title: place.n,
    });

    marker.setVisible(true);

    // if (!!selectedPlaces.length) {
    //   selectedPlaces.forEach(selectedPlace => {
    //     if (selectedPlace._id === place._id) {
    //       marker.setVisible(true);
    //     } else {
    //       marker.setVisible(false);
    //     }
    //   });
    // }

    listOfMarkerReferences.push(marker);

    markersAndPlacesDict[place._id] = {
      marker,
      place,
    };
  });

  return {listOfMarkerReferences, markersAndPlacesDict};
};

const googleMapStateReducer = (state, action) => {
  switch (action.type) {
    case LocationMapActionTypes.updateGoogleMapRelatedInstancesAndNamespace:
      return {
        ...state,
        googleMapNamespace: action.payload?.googleMapNamespace,
        autocompleteInstance: action.payload?.autocompleteInstance,
        distanceTimeService: action.payload?.distanceTimeService,
      };
    default:
      throw new Error(`${action.type} is not supported in ${googleMapStateReducer.name}`);
  }
};

// COMPONENT
const GoogleMap = ({
  lat,
  lng,
  selectedPlaces,
  refs,
  updateUserSearchedLocations,
  userSearchedLocations,
  showNearbyPlaces,
}) => {
  if (!(lat && lng)) {
    return null;
  }

  const hotelPosition = {
    lat,
    lng,
  };

  const dispatch = useDispatch();
  const [state, stateDispatch] = useReducer(googleMapStateReducer, locationMapInitState);
  const {googleMapNamespace, autocompleteInstance, distanceTimeService} = state;

  const googleMapsScriptLoaded = !!googleMapNamespace;

  const hotelName = useSelector(hotelNameSelectorFn);
  const {mapContainerRef, locationAutosuggestRef} = refs;

  useEffect(() => {
    dispatch(updateSelectedPlaces(userSearchedLocations));
  }, [userSearchedLocations]);

  useEffect(() => {
    if (!autocompleteInstance) {
      return;
    }

    autocompleteInstance.setFields(['name', 'geometry', 'id']);

    const onAutoCompleteSelection = async () => {
      const autocompleteSelectedPlace = autocompleteInstance.getPlace();
      if (!autocompleteSelectedPlace?.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.

        // 🐨opportunity for toast message
        return;
      }

      let normalisedUserSelectedPlace: Partial<SelectedPlace> = {};

      normalisedUserSelectedPlace.n = autocompleteSelectedPlace?.name || '';
      const userPlaceLat = autocompleteSelectedPlace?.geometry?.location.lat();
      const userPlaceLng = autocompleteSelectedPlace?.geometry?.location.lng();

      normalisedUserSelectedPlace.crd = [userPlaceLat, userPlaceLng];
      normalisedUserSelectedPlace._id = autocompleteSelectedPlace.id;
      normalisedUserSelectedPlace.type = GoogleMapLocationType.userSelected;

      const hotelGMLatLongObj = new googleMapNamespace.LatLng(hotelPosition.lat, hotelPosition.lng);

      const usesPlaceLatLngObj = new googleMapNamespace.LatLng(userPlaceLat, userPlaceLng);

      const promisifiedDistanceTimeService = (options, callback?) => {
        const p: Promise<{
          distanceFromHotel: string;
          timeFromHotel: string;
        }> = new Promise((resolve, reject) => {
          distanceTimeService.getDistanceMatrix(options, (response, status) => {
            const {distance, duration} = response?.rows?.[0].elements?.[0];

            if (!(status === 'OK')) {
              reject({
                error: '',
              });
            }

            resolve({
              distanceFromHotel: distance?.text,
              timeFromHotel: duration?.text,
            });
          });
        });
        return p;
      };

      let distanceAndTime: {
        distanceFromHotel?: string;
        timeFromHotel?: string;
      } = {};
      try {
        distanceAndTime = await promisifiedDistanceTimeService({
          origins: [hotelGMLatLongObj],
          destinations: [usesPlaceLatLngObj],
          travelMode: 'DRIVING',
        });
      } catch (e) {
        console.log(e);
      }

      normalisedUserSelectedPlace.distanceFromHotel = distanceAndTime.distanceFromHotel;
      normalisedUserSelectedPlace.timeFromHotel = distanceAndTime.timeFromHotel;
      // normalisedData.dist_display = /** to calculate */
      // time from hotel

      // dispatch(updateSelectedPlaces(normalisedUserSelectedPlace));
      updateUserSearchedLocations(normalisedUserSelectedPlace);
      // dispatch(updateSelectedPlaces(userSelectedPlacesState));
    };

    autocompleteInstance.addListener('place_changed', onAutoCompleteSelection);

    return () => {
      if (autocompleteInstance && autocompleteInstance.removeListener) {
        autocompleteInstance.removeListener(onAutoCompleteSelection);
      }
    };
  }, [
    autocompleteInstance,
    updateUserSearchedLocations,
    updateSelectedPlaces,
    // userSearchedLocations,
  ]);

  // load google maps script
  useEffect(() => {
    let googleMapsScript = document.createElement('script');

    const onLoad = () => {
      const googleMapNamespace = window.google.maps;
      const autocompleteInstance = new googleMapNamespace.places.Autocomplete(
        locationAutosuggestRef.current,
      );

      const distanceTimeService = new googleMapNamespace.DistanceMatrixService();

      stateDispatch({
        type: LocationMapActionTypes.updateGoogleMapRelatedInstancesAndNamespace,
        payload: {
          googleMapNamespace,
          autocompleteInstance,
          distanceTimeService,
        },
      });
    };

    const IntersectionObserverStencil = getIntersectionObserverInstance();

    const observer = new IntersectionObserverStencil(entries => {
      if (entries[0].isIntersecting === true) {
        if (typeof window === 'undefined') {
          return;
        }

        if (googleMapsScriptLoaded) {
          return;
        }

        googleMapsScript.async = true;
        googleMapsScript.defer = true;
        googleMapsScript.type = 'text/javascript';
        googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
        document.body.appendChild(googleMapsScript);

        googleMapsScript.addEventListener('load', onLoad);
      }
    }, intersectionObserverOptions);

    if (mapContainerRef && mapContainerRef.current) {
      observer.observe(mapContainerRef.current);
    }

    return () => {
      observer.disconnect();
      googleMapsScript.removeEventListener('load', onLoad);
    };
  }, [googleMapNamespace]);

  useEffect(() => {
    if (!googleMapsScriptLoaded || !mapContainerRef.current) {
      return;
    }

    // install location on our map container
    const hotelLocationMap = new googleMapNamespace.Map(mapContainerRef.current, {
      center: hotelPosition,
      zoom: 14,
      mapTypeControl: true,
      navigationControl: true,
    });

    autocompleteInstance.bindTo('bounds', hotelLocationMap);

    // if (autocompleteInstance) {
    //   autocompleteInstance.bindTo(hotelLocationMap);
    // }

    hotelLocationMap.setCenter(hotelPosition);

    // show hotel
    const hotelLocationMarker = new googleMapNamespace.Marker({
      position: hotelPosition,
      map: hotelLocationMap,
      icon: '//maps.google.com/mapfiles/kml/paddle/red-stars.png',
      zIndex: Number(defaultTheme.misc.zIndex.xsmall),
    });

    const hotelClickListeners = setInfoWindowOnMarker({
      content: getInfoWindowContent({name: hotelName}),
      googleMapNamespace,
      hotelLocationMap,
      marker: hotelLocationMarker,
      // showNearbyPlaces,
    });

    let allPlacesList = [];
    let markerClickListeners = hotelClickListeners;

    function recalculateStuff() {
      const {
        listOfMarkerReferences: allPlacesLocationMarkers,
        markersAndPlacesDict,
      } = setMarkersForAllPlaces({
        list: allPlacesList,
        googleMapNamespace,
        hotelLocationMap,
      });

      Object.keys(markersAndPlacesDict).map(placeId => {
        const place = markersAndPlacesDict[placeId]?.place;
        const marker = markersAndPlacesDict[placeId]?.marker;

        const content = getInfoWindowContent({name: place.n});
        const eventListeners = setInfoWindowOnMarker({
          content,
          googleMapNamespace,
          hotelLocationMap,
          marker,
          // showNearbyPlaces,
        });

        markerClickListeners.concat(eventListeners);
      });

      const bounds = new googleMapNamespace.LatLngBounds(hotelPosition);

      if (!!allPlacesLocationMarkers.length) {
        for (var i = 0; i < allPlacesLocationMarkers.length; i++) {
          bounds.extend(allPlacesLocationMarkers[i].getPosition());
        }

        hotelLocationMap.fitBounds(bounds);
      } else {
        hotelLocationMap.setZoom(14);
      }
    }

    if (!!selectedPlaces.length) {
      allPlacesList = selectedPlaces;
      recalculateStuff();
    }

    return () => {
      markerClickListeners.forEach(clickCbFn => {
        googleMapNamespace.event.removeListener(clickCbFn);
      });
    };
  }, [googleMapNamespace, selectedPlaces]);

  return (
    <>
      <MapGlobalStyles />
      <S.GoogleMapContainer ref={mapContainerRef} />
    </>
  );
};

export default React.memo(GoogleMap);